import { Level, Size } from "@/shared/components/generic/UiEnums";
import "./Buttons.scss";
import "@/shared/style/design_system/Fonts.scss";
import type { MouseEventHandler, ReactNode } from "react";
import { useMemo, useState } from "react";
import classNames from "classnames";
import type { IconComponent } from "@/shared/components/generic/IconComponentType.ts";
import { debounce } from "@mui/material";

interface Properties {
  icon: IconComponent;
  onClick: MouseEventHandler<HTMLElement>;
  level?: Level;
  size?: Size;
  disabled?: boolean;
  label?: string; // displayed only when size is XXXLARGE
  debounceDuration?: number;
}

export default function IconButton({
  icon,
  onClick,
  level = Level.PRIMARY,
  size = Size.MEDIUM,
  disabled = false,
  label = "",
  debounceDuration = 500,
}: Properties): ReactNode {
  function buttonClasses(): string {
    return classNames(
      "button",
      "cta-icon",
      "body",
      Level[level].toLocaleLowerCase(),
      Size[size].toLocaleLowerCase(),
      disabled && "disabled",
    );
  }

  const [isClickable, setIsClickable] = useState(true);

  const debouncedClickHandler = useMemo(
    () =>
      debounce((): void => {
        setIsClickable(true);
      }, debounceDuration),
    [debounceDuration],
  );

  return (
    <div
      className={classNames(buttonClasses())}
      onClick={(event: React.MouseEvent<HTMLElement>): void => {
        if (isClickable) {
          setIsClickable(false);
          onClick(event);
          debouncedClickHandler();
        }
      }}
    >
      {icon({ className: "icon" })}
      {size === Size.XXXLARGE && <div>{label}</div>}
    </div>
  );
}
